import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../api/auth/useAuth";
import {
  dismissTrialModalForCurrentLogin,
  wasTrialModalDismissedForCurrentLogin,
} from "../config/freeTrialPromo";
import { splitLocalePath } from "../i18n/localePath";
import { Paths } from "../router/paths";
import { useTrialOffer } from "./useTrialOffer";

const EMBEDDED_APP_PATHS: readonly string[] = [
  Paths.AKINDA_AI,
  Paths.GAMES,
  Paths.BACKTESTING,
];

/** Pages where a promo popup would get in the way of what the user came to do. */
function shouldSkipModal(pathname: string): boolean {
  if (EMBEDDED_APP_PATHS.includes(pathname)) return true;
  if (pathname === Paths.SUBSCRIPTIONS) return true;
  if (pathname === Paths.SUBSCRIPTION_RESULT) return true;
  if (pathname === Paths.PERSONAL_INFO) return true;
  if (pathname.startsWith("/payment-details")) return true;
  if (pathname.startsWith("/subscribe/")) return true;
  return false;
}

/**
 * The one-time "your free trial is ready" popup. It appears the first time an eligible
 * user opens the app after the trial unlocks (7 days after sign-up), once per login, and
 * never on checkout or subscription pages.
 */
export function useTrialReadyModal() {
  const { token } = useAuth();
  const { pathname: urlPathname } = useLocation();
  const pathname = splitLocalePath(urlPathname).pathname;
  const trial = useTrialOffer();

  const [dismissed, setDismissed] = useState(() =>
    wasTrialModalDismissedForCurrentLogin(),
  );

  useEffect(() => {
    setDismissed(wasTrialModalDismissedForCurrentLogin());
  }, [token]);

  const showModal =
    trial.isEligible && !trial.isLoading && !dismissed && !shouldSkipModal(pathname);

  const dismissModal = useCallback(() => {
    dismissTrialModalForCurrentLogin();
    setDismissed(true);
  }, []);

  return {
    /** The user may start a trial; used to give this popup priority over marketing ones. */
    isEligible: trial.isEligible,
    showModal,
    dismissModal,
  };
}
